import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  Modal,
  Stack,
  Text,
  Group,
  NumberInput,
  Textarea,
  Button,
  Alert,
  FileInput,
  Badge,
  ActionIcon,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCamera,
  IconUpload,
  IconFile,
  IconX,
} from '@tabler/icons-react';
import { DEFAULT_CURRENCY } from '../../constants/payment.consts';
import { DocumentScannerModal } from '@/components/DocumentScanner/DocumentScannerModal';

const acceptedFileTypes =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*';
interface RecordDepositModalProps {
  opened: boolean;
  onClose: () => void;
  remainingBalance: number;
  recordDeposit: (
    amount: number,
    note?: string,
    attachments?: Attachment[]
  ) => Promise<void>;
}

export const RecordDepositModal = ({
  opened,
  onClose,
  remainingBalance,
  recordDeposit,
}: RecordDepositModalProps) => {
  const { t } = useTranslation(['lims', 'common']);
  const medplum = useMedplum();

  const [depositForm, setDepositForm] = useState({ amount: 0, note: '' });
  const [files, setFiles] = useState<File[]>([]);
  const [scannerModalOpened, setScannerModalOpened] = useState(false);
  const [depositSaving, setDepositSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleDeposit = async () => {
    if (depositForm.amount <= 0) {
      return;
    }
    try {
      setDepositSaving(true);
      setLocalError(null);

      // Upload files first
      const uploadedAttachments = await Promise.all(
        files.map((file) =>
          medplum.createAttachment({
            data: file,
            filename: file.name,
            contentType: file.type,
          })
        )
      );

      await recordDeposit(
        depositForm.amount,
        depositForm.note,
        uploadedAttachments
      );
      handleClose();
    } catch (e: any) {
      setLocalError(e.message || 'Failed to record deposit');
    } finally {
      setDepositSaving(false);
    }
  };

  const handleClose = () => {
    setDepositForm({ amount: 0, note: '' });
    setFiles([]);
    setLocalError(null);
    onClose();
  };

  const handleFileUpload = (newFiles: File | File[] | null) => {
    if (!newFiles) {
      return;
    }
    const fileArray = Array.isArray(newFiles) ? newFiles : [newFiles];
    setFiles((prev) => [...prev, ...fileArray]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleScanComplete = (scannedFile: File) => {
    setFiles((prev) => [...prev, scannedFile]);
    setScannerModalOpened(false);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={t('payment.recordDeposit', { ns: 'lims' })}
        centered
        size="lg"
      >
        <Stack gap="md">
          {localError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {localError}
            </Alert>
          )}

          <NumberInput
            label={t('payment.labels.depositAmount', { ns: 'lims' })}
            min={0}
            max={remainingBalance}
            suffix={` ${DEFAULT_CURRENCY}`}
            value={depositForm.amount}
            onChange={(v) =>
              setDepositForm((prev) => ({ ...prev, amount: Number(v) || 0 }))
            }
          />

          <Text size="xs" c="dimmed">
            {t('payment.remainingAfterDeposit', {
              ns: 'lims',
              amount: Math.max(
                0,
                remainingBalance - depositForm.amount
              ).toFixed(2),
              currency: DEFAULT_CURRENCY,
            })}
          </Text>

          <Textarea
            label={t('payment.labels.noteText', { ns: 'lims' })}
            placeholder={t('payment.placeholders.enterNote', { ns: 'lims' })}
            value={depositForm.note}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setDepositForm((prev) => ({ ...prev, note: value }));
            }}
            autosize
          />

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('consent.fileUpload.title', {
                ns: 'lims',
                defaultValue: 'Supportive Documents',
              })}
            </Text>
            <Text size="sm" c="dimmed">
              {t('consent.fileUpload.description', {
                ns: 'lims',
                defaultValue:
                  'Upload signed consent forms or additional documents',
              })}
            </Text>

            <Group gap="sm" grow>
              <Button
                variant="light"
                leftSection={<IconCamera size={18} />}
                onClick={() => setScannerModalOpened(true)}
                disabled={depositSaving}
              >
                {t('documentScanner.title', {
                  ns: 'common',
                  defaultValue: 'Scan Document',
                })}
              </Button>

              <FileInput
                placeholder={t('consent.fileUpload.placeholder', {
                  ns: 'lims',
                  defaultValue: 'Click to select files',
                })}
                leftSection={<IconUpload size={18} />}
                accept={acceptedFileTypes}
                multiple
                onChange={handleFileUpload}
                disabled={depositSaving}
                value={undefined}
                key={files.length}
              />
            </Group>

            {files.length > 0 && (
              <Stack gap="xs" mt="sm">
                {files.map((file, index) => (
                  <Group
                    key={index}
                    justify="space-between"
                    p="xs"
                    bd="1px solid #e0e0e0"
                  >
                    <Group gap="xs">
                      <IconFile size={16} />
                      <Text size="sm">{file.name}</Text>
                      <Badge size="sm" variant="light">
                        {(file.size / 1024).toFixed(1)} KB
                      </Badge>
                    </Group>
                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="subtle"
                      onClick={() => removeFile(index)}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose}>
              {t('payment.cancel', { ns: 'lims' })}
            </Button>
            <Button
              onClick={handleDeposit}
              loading={depositSaving}
              disabled={depositForm.amount <= 0}
            >
              {t('payment.confirm', { ns: 'lims' })}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <DocumentScannerModal
        opened={scannerModalOpened}
        onClose={() => setScannerModalOpened(false)}
        onScanComplete={handleScanComplete}
      />
    </>
  );
}
